import axios from "axios";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import moment from "moment";
import LoginFun from './../../Layout Component/Loginfun';
import Swal from "sweetalert2";
import './TreatmentWithUs.css';
import { useDispatch } from "react-redux";
import { setLoader } from "../../Store/Reducers/UserReducer"
import MedicalHistoryNav from "./MedicalHistoryNav";
import { setTreatmentsOpenedFrom } from "./medicalHistoryPages";
import { readFolliculogramVersions, parseFolliDate } from "./Folliculogram";
import { useContextMenu } from "../../Layout Component/ContextMenu";

// ── Treatment with Us: the patient's treatment cycles ────────────────────────
//
// A table of the cycles, ten to a page, laid out like the Treatments tab in the
// OX.ar hub: the cycle, when it started, the span its folliculogram covers,
// its type, the outcome, the embryology counts and when it was created — with
// a line of detail under each row, whose metrics depend on the treatment type
// (see metricsFor — the same rule as the hub). Every column
// sorts (dates by their value, never as text), and the search looks at all of
// them. Details and Images open the same pages they always did.
//
// WHERE THE COLUMNS COME FROM. The list call (GetTreatmentCyclesByContactId)
// returns the Dataverse columns for each cycle — bcrm_treatment_start_date,
// bcrm_total_collected, … — and, for each option set, its label beside the
// code (bcrm_treatment_type_os_name, bcrm_pregnant_name, bcrm_outcome_name),
// which is what is shown. The one
// thing it does not carry is the folliculogram, so the folliculogram dates come
// from the single-cycle call the Details page makes (GetTreatmentCycleById),
// fetched for each cycle in the background, a few at a time. Only that column
// waits; the rest of the row is there at once.
//
// A count column no cycle has a value for is left off rather than shown as a
// column of dashes.

const PAGE_SIZE = 10;
const DETAIL_CONCURRENCY = 4;

// Nothing recorded. The portal API writes a missing value as the TEXT "N/A"
// as often as it leaves it out, and a chip reading "N/A" or a detail reading
// "Dose: N/A" is a blank dressed up as an answer.
function isBlank(v) {
  if (v === null || v === undefined) return true;
  const s = String(v).trim().toLowerCase();
  return s === "" || s === "n/a" || s === "na" || s === "null" || s === "undefined";
}

// The first of several field names that has a value. The portal DTOs are not
// consistent about naming, and a column should not go blank because one
// endpoint says typeofCycle and another typeOfCycle.
function pick(record, keys) {
  for (const key of keys) {
    const v = record?.[key];
    if (!isBlank(v)) return v;
  }
  return null;
}

// A field found by what it is CALLED rather than its exact spelling. The same
// number arrives as totalCollected, bcrm_total_collected or TotalCollected
// depending on which DTO it came through, and the portal API is not
// documented here — so names are compared with case, "bcrm_" and every
// non-letter stripped. Where the API sends a readable label beside a code
// (fooName, foo_name, or the OData formatted value), the label wins: a patient
// should read "Cycle Cancelled", not 271400003.
const squash = (s) => String(s).toLowerCase().replace(/^bcrm_?/, "").replace(/[^a-z0-9]/g, "");

function pickLoose(record, aliases) {
  if (!record) return null;
  const wanted = aliases.map(squash);
  for (const key of Object.keys(record)) {
    if (key.includes("@")) continue;
    if (!wanted.includes(squash(key))) continue;
    const label = [`${key}@OData.Community.Display.V1.FormattedValue`, `${key}Name`, `${key}_name`]
      .map((k) => record[k])
      .find((v) => !isBlank(v));
    const value = label ?? record[key];
    if (!isBlank(value)) return value;
  }
  return null;
}

// The metrics a cycle's detail line can show. The first name for each is the
// field the OX.ar hub reads (treatmentDisplay.js, cycleMetricChips); the rest
// are other spellings the portal API may use for the same thing.
const METRICS = [
  ["Collected", ["totalCollected", "total_collected", "numberCollected"]],
  ["IVF no. mature day1", ["ivfNumberPbAtPn", "ivf_number_pb_at_pn", "ivfNoMatureDay1"]],
  ["Injected Day0", ["injectedDay0", "injected_day0"]],
  ["On time 2pns", ["onTime2pns", "on_time_2pns"]],
  ["Number transferred", ["numberTransferred", "number_transferred"]],
  ["Number frozen", ["numberFrozen", "number_frozen"]],
  // Read straight from their label columns in toRow, not looked up here.
  ["Pregnant", []],
  ["Outcome", []],
];

// WHICH METRICS A CYCLE SHOWS — exactly the hub's rule (treatmentDisplay.js,
// ported there from the legacy Patient Overview), so the portal and the hub
// agree field-for-field:
//
//   FET             → Number transferred
//   IVF (not ICSI)  → Collected, IVF no. mature day1, On time 2pns,
//                     Number transferred, Number frozen
//   ICSI / IVF-ICSI → Collected, Injected Day0, On time 2pns,
//                     Number transferred, Number frozen
//   IUI             → Collected, On time 2pns, Number transferred, Number frozen
//   anything else   → no metrics (including a type the API leaves blank)
//
// then Pregnant and Outcome whenever they are set, whatever the type. A metric
// in the type's list with no value reads N/A rather than being dropped: on a
// lab record "we didn't record it" and "it didn't happen" are different, and
// hiding the row would make them look the same. The checks run in this order
// on purpose — FET first, and IVF only when it is not IVF/ICSI.
function metricsFor(type, values) {
  const t = type || "";
  const isIvfIcsi = /IVF\/ICSI/i.test(t);
  let labels;
  if (/FET/i.test(t)) {
    labels = ["Number transferred"];
  } else if (/IVF/i.test(t) && !isIvfIcsi) {
    labels = ["Collected", "IVF no. mature day1", "On time 2pns", "Number transferred", "Number frozen"];
  } else if (/ICSI/i.test(t) || isIvfIcsi) {
    labels = ["Collected", "Injected Day0", "On time 2pns", "Number transferred", "Number frozen"];
  } else if (/IUI/i.test(t) || /insemination/i.test(t)) {
    labels = ["Collected", "On time 2pns", "Number transferred", "Number frozen"];
  } else {
    labels = [];
  }
  if (values.Pregnant !== null) labels.push("Pregnant");
  if (values.Outcome !== null) labels.push("Outcome");
  return labels.map((label) => [label, values[label]]);
}

// "0001-01-01" is how the API writes "no date".
function dateValue(raw) {
  if (isBlank(raw) || String(raw).startsWith("0001-01-01")) return null;
  const m = moment(raw);
  // Year 1 in any other spelling ("01-Jan-0001") is the same "no date".
  return m.isValid() && m.year() > 1 ? m : null;
}

const fmt = (m) => (m ? m.format("DD-MMM-YYYY") : "");

// Short name and colour slot per treatment type. The label is always shown, so
// the colour is a second signal and never the only one.
const TYPE_STYLES = [
  [/ivf\s*\/\s*icsi/i, "IVF/ICSI", "t-ivficsi"],
  [/icsi/i, "ICSI", "t-icsi"],
  [/ivf/i, "IVF", "t-ivf"],
  [/fet|frozen/i, "FET", "t-fet"],
  [/iui|insemination/i, "IUI", "t-iui"],
  [/thaw/i, null, "t-thaw"],
  [/vot|ift/i, null, "t-vot"],
];

// A small icon beside each type, so the type reads without the colour. See
// docs/treatment-colours.md.
const TYPE_ICONS = {
  "t-ivficsi": "fa-flask",
  "t-icsi": "fa-microscope",
  "t-ivf": "fa-vial",
  "t-fet": "fa-snowflake",
  "t-iui": "fa-droplet",
  "t-thaw": "fa-temperature-half",
  "t-vot": "fa-right-left",
  "t-other": "fa-notes-medical",
};

function TypeChip({ type }) {
  if (!type) return <span className="twu-dash">—</span>;
  const match = TYPE_STYLES.find(([re]) => re.test(type));
  const label = match?.[1] || type;
  const cls = match?.[2] || "t-other";
  return (
    <span className={`twu-type ${cls}`} title={type}>
      <i className={`fa-solid ${TYPE_ICONS[cls]}`} aria-hidden="true" />
      {label}
    </span>
  );
}

// First and last scan day of the latest folliculogram version.
function folliculogramSpan(json) {
  const versions = readFolliculogramVersions(json);
  const days = versions[versions.length - 1]?.days || [];
  const dates = days.map((d) => parseFolliDate(d.date)).filter(Boolean).sort((a, b) => a - b);
  if (!dates.length) return null;
  return { from: moment(dates[0]), to: moment(dates[dates.length - 1]) };
}

// The couple a cycle belongs to — the patient, and their partner when there is
// one — as the hub prints it after the cycle's type and date.
export function coupleName() {
  let patient = "";
  let partner = "";
  try {
    const u = JSON.parse(localStorage.getItem("UData"));
    patient = [u?.firstName, u?.lastName].filter(Boolean).join(" ");
  } catch {
    patient = "";
  }
  try {
    partner = JSON.parse(sessionStorage.getItem("ReleatedcontactData"))?.fullname || "";
  } catch {
    partner = "";
  }
  return [patient, partner].filter(Boolean).join(" & ");
}

// One cycle, as the table reads it. Everything comes from the list row
// except the folliculogram dates, which need the cycle's own call.
//
// The option sets — treatment type, pregnant, outcome — are read from the
// label columns the API sends beside each code (bcrm_treatment_type_os_name,
// bcrm_pregnant_name, bcrm_outcome_name), so every value shows exactly as
// Dataverse names it and a new option needs no change here.
export function toRow(cycle, detail, couple) {
  const start = dateValue(pick(cycle, ["bcrm_treatment_start_date", "treatmentStartDate"]));
  const created = dateValue(pick(cycle, ["createdOn", "createdon"]));
  const span = detail ? folliculogramSpan(detail.folliculagramJSON) : null;

  // The TREATMENT type (IVF, ICSI, FET…). bcrm_type_of_cycle_name is something
  // else — the stimulation protocol (Antagonist, Natural…) — and is not used.
  const type = pick(cycle, ["bcrm_treatment_type_os_name"]);

  // Every metric's value (null when there is none), by label. 0 is a value:
  // "none frozen" is an answer.
  const values = {};
  METRICS.forEach(([label, aliases]) => {
    const v = pickLoose(cycle, aliases);
    values[label] = v === null ? null : String(v);
  });
  values.Pregnant = pick(cycle, ["bcrm_pregnant_name"]);
  values.Outcome = pick(cycle, ["bcrm_outcome_name"]);

  // THE NAME, the way the hub names a cycle: its type and start date, then
  // whose it is. The stored name is kept for the tooltip and the search, and
  // is used as-is when the cycle has no known type or no start date. A cycle
  // with no stored name either is named by when it was created, so no row is
  // left with an empty first column.
  const stored = isBlank(cycle.name) ? "" : cycle.name;
  const title = type && start
    ? `${type} ${start.format("DD-MMM-YYYY")}`
    : stored || (created ? `Treatment ${fmt(created)}` : "Treatment");

  return {
    id: cycle.id,
    name: stored,
    title,
    couple: type && start ? couple : "",
    loaded: Boolean(detail),
    type,
    start,
    span,
    // The hub's Outcome column: the outcome, or failing that the pregnancy
    // answer.
    outcome: values.Outcome ?? values.Pregnant,
    collected: values.Collected,
    transferred: values["Number transferred"],
    frozen: values["Number frozen"],
    created,
    metrics: metricsFor(type, values),
  };
}

const COLUMNS = [
  { key: "name", label: "Cycle" },
  { key: "start", label: "Start", date: true },
  { key: "span", label: "Folliculogram dates", date: true },
  { key: "type", label: "Type" },
  { key: "outcome", label: "Outcome" },
  { key: "collected", label: "Collected", numeric: true, optional: true },
  { key: "transferred", label: "Transferred", numeric: true, optional: true },
  { key: "frozen", label: "Frozen", numeric: true, optional: true },
  { key: "created", label: "Created", date: true, withTime: true },
];

// The value a column sorts on: a timestamp for dates, a number for counts,
// otherwise the text. Null means "nothing here".
function sortValue(row, col) {
  // The Cycle column sorts on the name it shows, not the stored one.
  const v = col.key === "name" ? row.title || row.name : row[col.key];
  if (v === null || v === undefined || v === "") return null;
  if (col.key === "span") return v.from.valueOf();
  if (col.date) return v.valueOf();
  if (col.numeric) return Number.isFinite(Number(v)) ? Number(v) : null;
  return String(v).toLowerCase();
}

function cellText(row, col) {
  const v = row[col.key];
  if (col.key === "span") return v ? `${fmt(v.from)} – ${fmt(v.to)}` : "";
  if (col.withTime) return v ? v.format("DD-MMM-YYYY HH:mm") : "";
  if (col.date) return fmt(v);
  return v === null || v === undefined ? "" : String(v);
}

// The patient's name, date of birth, age and gender, as the Medical History
// heading prints it — so the standalone page still says whose treatments these
// are.
function patientLine() {
  try {
    const u = JSON.parse(localStorage.getItem("UData"));
    if (!u) return "";
    const dob = u.birthday ? moment(u.birthday).format("DD-MMM-YYYY") : "";
    return [
      [u.firstName, u.lastName].filter(Boolean).join(" "),
      dob && `${dob}${u.age !== undefined && u.age !== null ? ` (${u.age})` : ""}`,
      u.gendercodeName,
    ].filter(Boolean).join(", ");
  } catch {
    return "";
  }
}

// TWO WAYS TO SHOW THE SAME LIST.
//
//   /TreatMentWithUs  — the "Treatment with Us" tab inside Medical History:
//                       under the Medical History heading and its tab bar.
//   /Treatments       — the sidebar's own "Treatment with Us" (`standalone`):
//                       a page of its own, with its own heading and nothing of
//                       Medical History around it.
//
// Whichever it is, it is recorded as the way in, so the Details and Images
// pages opened from the list keep the right sidebar item lit — including when
// the patient comes back with the browser's Back button, which a click
// handler on the link would miss.
export default function TreatmentWithUs({ standalone = false, embedded = false }) {
  const dispatch = useDispatch();
  const url = process.env.REACT_APP_OXAR_API_URL;
  const logindata = LoginFun();
  const navigate = useNavigate();

  useEffect(() => {
    setTreatmentsOpenedFrom(embedded ? "overview" : standalone ? "sidebar" : "medicalHistory");
  }, [standalone, embedded]);

  const [cycles, setCycles] = useState([]);
  const [details, setDetails] = useState({});
  const [loaded, setLoaded] = useState(false);
  const [search, setSearch] = useState("");
  // Newest start first, as the hub opens its treatment list.
  const [sort, setSort] = useState({ key: "start", dir: "desc" });
  const [page, setPage] = useState(1);

  const openDetails = (id) => {
    localStorage.setItem("Tretmentid", id);
    navigate("/TreatmentOutcome", { state: { Tid: id } });
  };

  const openImages = (id, name) => {
    sessionStorage.setItem("Treatmentid", id);
    sessionStorage.setItem("TreatmentName", name);
    navigate("/DicomViewer");
  };

  // The read-only cycle page: the folliculogram and the images, nothing to edit.
  const openCycle = (row, tab = "folliculogram") => {
    localStorage.setItem("Tretmentid", row.id);
    sessionStorage.setItem("Treatmentid", row.id);
    sessionStorage.setItem("TreatmentName", row.name || row.title);
    sessionStorage.setItem("TreatmentTitle", row.title);
    navigate("/Cycle", {
      state: {
        id: row.id,
        name: row.name || row.title,
        title: row.title,
        couple: row.couple,
        range: row.span ? `${fmt(row.span.from)} \u2013 ${fmt(row.span.to)}` : "",
        type: row.type,
        start: row.start ? fmt(row.start) : "",
        tab,
      },
    });
  };

  const { show: showMenu, menu: rowMenu } = useContextMenu();
  const [selectedId, setSelectedId] = useState(null);
  const rowMenuItems = (row) => [
    { label: "Open cycle", hint: "double-click", onClick: () => openCycle(row, "folliculogram") },
    { label: "View folliculogram", onClick: () => openCycle(row, "folliculogram") },
    { label: "View images", onClick: () => openCycle(row, "images") },
    { divider: true },
    { label: "Details and outcome", onClick: () => openDetails(row.id) },
    { label: "Refresh list", onClick: () => getTreatmentCycles() },
  ];

  useEffect(() => {
    const currentTime = Math.round(new Date().getTime() / 1000);
    const Expiretime = localStorage.getItem("ExpirretokenTime");
    if (Expiretime < currentTime) {
      dispatch(setLoader(false));
      Swal.fire({
        title: 'Session Expired',
        text: "Your session has been expired! Please login again",
        icon: 'warning',
        showCancelButton: false,
        confirmButtonText: 'Okay',
      }).then(() => {
        logindata.logout();
      });
    }
    getTreatmentCycles();
    // Once, on opening the page.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getTreatmentCycles = async () => {
    dispatch(setLoader(true));
    let list = [];
    try {
      const MainUserId = localStorage.getItem("UserID");
      const res = await axios(`${url}api/Treatment/GetTreatmentCyclesByContactId?id=${MainUserId}`);
      list = res.data?.content?.treatmentcycleDTO || [];
      setCycles(list);
    } catch {
      setCycles([]);
    } finally {
      setLoaded(true);
      dispatch(setLoader(false));
    }
    loadDetails(list);
  };

  // Each cycle's details, a few at a time, each row filling in as its own
  // answer lands. A cycle whose call fails keeps its list columns and shows
  // blanks for the rest rather than holding up the others.
  const loadDetails = async (list) => {
    const queue = [...list];
    const worker = async () => {
      while (queue.length) {
        const cycle = queue.shift();
        let detail = {};
        try {
          const res = await axios(`${url}api/Treatment/GetTreatmentCycleById?id=${cycle.id}`);
          detail = res.data?.content || {};
        } catch {
          detail = {};
        }
        setDetails((prev) => ({ ...prev, [cycle.id]: detail }));
      }
    };
    await Promise.all(Array.from({ length: Math.min(DETAIL_CONCURRENCY, list.length) }, worker));
  };

  const couple = useMemo(coupleName, []);
  const rows = useMemo(
    () => cycles.map((c) => toRow(c, details[c.id], couple)),
    [cycles, details, couple],
  );

  // The cycle in progress: the most recent one with no outcome recorded yet
  // (the clinical Treatments tab marks it ACTIVE).
  const activeId = useMemo(() => {
    const open = rows.filter((r) => r.type && r.start && !r.outcome);
    open.sort((a, b) => b.start.valueOf() - a.start.valueOf());
    return open[0]?.id || null;
  }, [rows]);

  // Optional columns only where at least one cycle has a value.
  const columns = useMemo(
    () => COLUMNS.filter((c) => !c.optional || rows.some((r) => r[c.key] !== null)),
    [rows],
  );

  const shown = useMemo(() => {
    const term = search.trim().toLowerCase();
    const filtered = term
      ? rows.filter((r) =>
          [
            r.name,
            r.title,
            r.couple,
            ...columns.map((c) => cellText(r, c)),
            ...r.metrics.map(([, v]) => v),
          ]
            .filter(Boolean)
            .join(" ")
            .toLowerCase()
            .includes(term),
        )
      : rows;

    const col = COLUMNS.find((c) => c.key === sort.key);
    if (!col) return filtered;
    const dir = sort.dir === "asc" ? 1 : -1;
    return [...filtered].sort((a, b) => {
      const left = sortValue(a, col);
      const right = sortValue(b, col);
      // Nothing recorded sorts as the oldest / smallest, so it is at the top
      // oldest-first and the bottom newest-first.
      if (left === null && right === null) return 0;
      if (left === null) return -dir;
      if (right === null) return dir;
      if (typeof left === "string") return left.localeCompare(right) * dir;
      return (left - right) * dir;
    });
  }, [rows, columns, search, sort]);

  const pages = Math.max(1, Math.ceil(shown.length / PAGE_SIZE));
  const current = Math.min(page, pages);
  const pageRows = shown.slice((current - 1) * PAGE_SIZE, current * PAGE_SIZE);
  const firstShown = shown.length ? (current - 1) * PAGE_SIZE + 1 : 0;
  const lastShown = Math.min(current * PAGE_SIZE, shown.length);

  const setSortKey = (key) => {
    setSort((s) => (s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }));
    setPage(1);
  };

  const stillLoading = cycles.some((c) => !details[c.id]);

  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      {embedded ? null : standalone ? (
        // The same heading markup as Medical History's (MedicalHistoryNav),
        // so the page looks like the rest of the portal — without the tabs.
        <div className="card-header mb-2">
          <div className="row">
            <div className="col">
              <h5>Treatment with Us</h5>
            </div>
            {patientLine() && <div className="col text-right">( {patientLine()} )</div>}
          </div>
        </div>
      ) : (
        <MedicalHistoryNav />
      )}
      <section className="twu">
        <div className="twu-head">
          <h5>Treatments (with us)</h5>
          <button type="button" className="twu-btn" onClick={getTreatmentCycles}>
             Refresh
          </button>
        </div>

        <div className="twu-bar">
          <input
            type="search"
            className="twu-search"
            placeholder="Search cycles…"
            aria-label="Search treatment cycles"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
          />
          <span className="twu-count">
            {search ? `${shown.length} of ${cycles.length}` : cycles.length} cycle{cycles.length === 1 ? "" : "s"}
          </span>
          {stillLoading && cycles.length > 0 && <span className="twu-loading">Loading treatment details…</span>}
        </div>

        {loaded && cycles.length === 0 ? (
          <p className="twu-empty">No treatment cycles on record.</p>
        ) : (
          <div className="twu-scroll">
            <table className="twu-table">
              <thead>
                <tr>
                  {columns.map((col) => {
                    const on = sort.key === col.key;
                    return (
                      <th key={col.key} className={col.numeric ? "is-num" : undefined}>
                        <button type="button" className={`twu-sort${on ? " is-on" : ""}`} onClick={() => setSortKey(col.key)}>
                          {col.label}
                          <span aria-hidden="true">{on ? (sort.dir === "asc" ? " ?" : " ?") : " ?"}</span>
                        </button>
                      </th>
                    );
                  })}
                  <th className="twu-actions-h"><i className="fa-solid fa-wave-square" aria-hidden="true" /> Folliculogram</th>
                  <th className="twu-actions-h"><i className="fa-solid fa-images" aria-hidden="true" /> Images</th>
                </tr>
              </thead>
              <tbody>
                {pageRows.length === 0 && (
                  <tr>
                    <td colSpan={columns.length + 2} className="twu-empty-cell">
                      No cycle matches that search.
                    </td>
                  </tr>
                )}
                {pageRows.map((row) => {
                  return (
                    <React.Fragment key={row.id}>
                      <tr
                        className={`twu-row${selectedId === row.id ? " is-selected" : ""}`}
                        tabIndex={0}
                        title="Click to select. Double-click to open the folliculogram and images. Right-click for more."
                        onClick={() => setSelectedId(row.id)}
                        onDoubleClick={() => openCycle(row)}
                        onKeyDown={(e) => { if (e.key === "Enter") openCycle(row); }}
                        onContextMenu={(e) => { setSelectedId(row.id); showMenu(e, rowMenuItems(row)); }}
                      >
                        {columns.map((col) => {
                          if (col.key === "name") {
                            // Type and date in bold, the couple after it in
                            // grey — two spans, so a name that does not fit
                            // wraps between them, never through a surname.
                            return (
                              <td key={col.key} className="twu-name" title={row.name}>
                                <strong>{row.title}</strong>
                                {row.couple && <span className="twu-name-for">{row.couple}</span>}
                                {row.id === activeId && <span className="twu-active">Active</span>}
                              </td>
                            );
                          }
                          if (col.key === "type") {
                            return (
                              <td key={col.key}>
                                <TypeChip type={row.type} />
                              </td>
                            );
                          }
                          const text = cellText(row, col);
                          // Only the folliculogram dates come from a second
                          // call; everything else is on the list row.
                          const waiting = !row.loaded && col.key === "span";
                          return (
                            <td key={col.key} className={`${col.numeric ? "is-num" : ""}${col.date ? " is-date" : ""}`}>
                              {text || (waiting ? <span className="twu-wait">…</span> : <span className="twu-dash">—</span>)}
                            </td>
                          );
                        })}
                        <td className="twu-actions">
                          <button type="button" className="twu-link twu-iconlink" title="See the folliculogram for this treatment" onClick={(e) => { e.stopPropagation(); openCycle(row, "folliculogram"); }}>
                            <i className="fa-solid fa-wave-square" aria-hidden="true" /> View
                          </button>
                        </td>
                        <td className="twu-actions">
                          <button type="button" className="twu-link twu-iconlink" title="See the scan images for this treatment" onClick={(e) => { e.stopPropagation(); openCycle(row, "images"); }}>
                            <i className="fa-solid fa-images" aria-hidden="true" /> View
                          </button>
                        </td>
                      </tr>
                      {row.metrics.length > 0 && (
                        <tr
                          className={`twu-detail${selectedId === row.id ? " is-selected" : ""}`}
                          onClick={() => setSelectedId(row.id)}
                          onDoubleClick={() => openCycle(row)}
                          onContextMenu={(e) => { setSelectedId(row.id); showMenu(e, rowMenuItems(row)); }}
                        >
                          <td colSpan={columns.length + 2}>
                            {row.metrics.map(([label, v]) => (
                              <span key={label} className="twu-fact">
                                {label}: {v === null ? <span className="twu-na">N/A</span> : <strong>{v}</strong>}
                              </span>
                            ))}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {shown.length > PAGE_SIZE && (
          <nav className="twu-pager" aria-label="Treatment pages">
            <span className="twu-pager-count">
              {firstShown}–{lastShown} of {shown.length}
            </span>
            <button type="button" className="twu-btn" onClick={() => setPage(current - 1)} disabled={current === 1}>
              ‹ Previous
            </button>
            {Array.from({ length: pages }, (_, i) => i + 1).map((n) => (
              <button
                type="button"
                key={n}
                className={`twu-btn twu-page${n === current ? " is-on" : ""}`}
                onClick={() => setPage(n)}
                aria-current={n === current ? "page" : undefined}
              >
                {n}
              </button>
            ))}
            <button type="button" className="twu-btn" onClick={() => setPage(current + 1)} disabled={current === pages}>
              Next ›
            </button>
          </nav>
        )}
      </section>
      {rowMenu}
    </div>
  );
}
